import { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthenticatedFormData } from '../components/save-project-dialog/save-project-dialog-types';
import { useAnalyticsContext } from '../context/analytics-context';
import { getSaveStatusMessage } from '../context/analytics-context/utils/get-save-status-message';
import { setIsSaving, useAppContext } from '../context/app-context';
import { useInitializationDataContext } from '../context/data-context';
import { getLoginUrl, isLoggedIn, navigateTo, triggerAbandonOrDeleteProjectEvent, getMonolithUrl } from '../utils';
import { savePersonalization } from '../utils/save-project';
import { useProjectSummary } from './use-project-summary';
import { useQueryParams } from './use-query-params';
import { useSaveProject } from './use-save-project';

export const useSaveProjectDialog = () => {
  const {
    trackSignIn,
    trackEditProjectName,
    trackDefaultProjectName,
    trackExitFlow,
    trackAbandonOrDeleteProject,
    trackSaveLink,
  } = useAnalyticsContext();
  const { defaultProjectName } = useProjectSummary();
  const { saveDraftName, saveProject } = useSaveProject();
  const {
    initializedDataState: { isUK },
  } = useInitializationDataContext();

  const { appDispatch, appState } = useAppContext();

  const { exitUrl: exitUrlForAbandon } = appState;

  const { pathname } = useLocation();
  const isEditorView = pathname.includes('edit');

  const navigate = useNavigate();

  const queryParams = useQueryParams();
  const exitUrl = queryParams.get('exitUrl');
  const isAbandoningProject = queryParams.get('exitUrl') !== null;

  const [isExitFromXButton, setIsExitFromXButton] = useState(false);

  const isSaveProjectDialogOpen =
    queryParams.get('saveProjectDialog') === 'true' || queryParams.get('exitUrl') !== null;

  const hasUpdatedDraftName = useRef(false);
  const {
    initializedDataState: { data: initializedData, lineItemUUID },
  } = useInitializationDataContext();

  const handleClose = () => {
    queryParams.delete('saveProjectDialog');
    queryParams.delete('exitUrl');
    navigate({ pathname, search: queryParams.toString() }, { replace: true });
  };

  const onCloseDialog = () => {
    // Closing the abandon modal with the X counts as an exit; the save/sign-in modal is a cancel.
    if (isAbandoningProject) {
      trackExitFlow();
    } else {
      trackSignIn('cancel');
    }
    handleClose();
  };

  const handleExitFlow = (url: string) => {
    if (url) {
      navigateTo(url);
    }
  };

  const onSaveProject = async (data: AuthenticatedFormData) => {
    handleClose();
    setIsSaving(appDispatch, true);
    // allSettled so a failed personalization save does not cut short the draft name
    // request that is already in flight, nor skip the exit flow below.
    const results = await Promise.allSettled([
      savePersonalization(
        { shouldRestoreCanvas: true, showLoadingScreen: true, generateFrontPreview: false, throwOnError: true },
        isEditorView,
        saveProject,
      ),
      saveDraftName(data.projectName, true),
    ]);
    const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failures.length) {
      trackSaveLink('fail', getSaveStatusMessage(failures.map((failure) => failure.reason)));
    } else if (!defaultProjectName) {
      // The default name hasn't loaded yet, so we can't reliably tell whether the user edited it.
      // Fall back to a generic success event instead of misreporting this as an edit.
      trackSaveLink('success');
    } else if (data.projectName === defaultProjectName) {
      trackDefaultProjectName();
    } else {
      trackEditProjectName();
    }
    hasUpdatedDraftName.current = true;
    setIsSaving(appDispatch, false);
    handleExitFlow(exitUrl || '');
  };

  const onSignIn = () => {
    trackSignIn('sign in');
    handleClose();
    savePersonalization(
      { shouldRestoreCanvas: false, showLoadingScreen: true, generateFrontPreview: false },
      isEditorView,
      saveProject,
    ).then(() => {
      navigateTo(loginUrl);
    });
  };

  const onAbandonProject = async () => {
    if (isLoggedIn() && initializedData?.name) {
      await saveDraftName(initializedData.name, false);
    }
    trackAbandonOrDeleteProject(triggerAbandonOrDeleteProjectEvent());
    const redirectUrl = !isExitFromXButton ? getMonolithUrl() : exitUrlForAbandon;
    handleExitFlow(redirectUrl || '');
  };

  const loginUrl = getLoginUrl(
    initializedData?.project_id as string,
    initializedData?.account_id as string,
    lineItemUUID,
    isUK,
  );

  return {
    isSaveProjectDialogOpen,
    isEditorView,
    isAbandoningProject,
    onCloseDialog,
    onSignIn,
    onAbandonProject,
    onSaveProject,
    setIsExitFromXButton,
    isExitFromXButton,
  };
};
